<template>
  <section
    id="articles-section"
    class="articles-module-component">
    <div class="article-inner-container py-11">
      <div
        v-if="data.articles.show_filter"
        class="categories-container pb-8 lg:pb-10">
        <div
          v-if="!initialLoading"
          class="categories">
          <ul
            class="categories-list mx-auto flex w-fit flex-col rounded-[30px] border border-l
              border-black p-1 lg:flex-row lg:rounded-full">
            <template
              v-for="(category, idx) of allCategories"
              :key="`category-${idx}`">
              <li
                v-if="category.count && category.count > 0"
                class="category min-w-max cursor-pointer rounded-full px-5 py-5 text-center
                  transition-colors duration-500 ease-in-out lg:px-10 xl:min-w-[280px]"
                :class="
                  category.databaseId === currentCat.databaseId
                    ? 'bg-bicara-yellow'
                    : 'bg-transparent'
                "
                @click="setCurrentCat(category)">
                <div class="body-md">
                  {{ category.name }}
                </div>
              </li>
            </template>
          </ul>
        </div>
        <div
          v-else
          class="loading-transition">
          <div
            role="status"
            class="mx-auto flex w-fit animate-pulse flex-col gap-2 rounded-3xl border border-l
              border-black p-1 lg:flex-row lg:rounded-full">
            <div
              class="h-14 w-72 rounded-full bg-gray-200 dark:bg-gray-300"></div>
            <div
              class="h-14 w-72 rounded-full bg-gray-200 dark:bg-gray-300"></div>
            <div
              class="h-14 w-72 rounded-full bg-gray-200 dark:bg-gray-300"></div>
            <div
              class="h-14 w-72 rounded-full bg-gray-200 dark:bg-gray-300"></div>
            <span class="sr-only">Loading...</span>
          </div>
        </div>
      </div>
      <div v-else>
        <h2 class="header-5 pb-8 pt-8 text-bicara-charcoal">
          {{ data.articles.title }}
        </h2>
      </div>
      <div
        class="articles-container rounded-2xl border border-black p-lg lg:p-10">
        <div class="article-columns flex w-full flex-wrap">
          <template v-if="!dataLoading">
            <article
              v-for="article of postData"
              :key="article.databaseId">
              <p class="body-md line-clamp-3 min-h-14 lg:line-clamp-2">
                {{ article.title }}
              </p>
              <div class="mb-6 flex border-b border-black pb-6">
                <div
                  class="bottom-area flex w-full items-center justify-between">
                  <div class="flex flex-col lg:flex-row">
                    <p
                      class="metadata mb-0 mr-2 block min-w-[130px] pb-1 pl-2 lg:hidden">
                      {{ formatDate(article.date) }}
                    </p>
                    <div
                      class="bubble-button flex h-fit w-fit rounded-full border border-black px-4 py-2">
                      <p
                        class="metadata mb-0 mr-2 hidden min-w-[130px] border-r border-black pr-2 lg:block">
                        {{ formatDate(article.date) }}
                      </p>
                      <p
                        class="metadata mb-0 line-clamp-1 text-bicara-blue xl:line-clamp-none">
                        {{ article.categories.nodes[0].name }}
                      </p>
                    </div>
                  </div>
                  <a
                    :href="
                      article.postAcf.is_external
                        ? article.postAcf.external_link
                        : article.link
                    "
                    :target="article.postAcf.is_external ? '_blank' : '_self'"
                    class="flex w-fit">
                    <svg
                      width="45"
                      height="45"
                      viewBox="0 0 45 45"
                      fill="none"
                      class="article-arrow-button ml-2"
                      :class="`${article.postAcf.is_external ? '' : 'rotate-45'}`">
                      <circle
                        cx="22.5"
                        cy="22.5"
                        r="22"
                        stroke="#0A2238" />
                      <circle
                        cx="22.5"
                        cy="22.5"
                        r="15.9375"
                        class="article-perfect-circle"
                        fill="#FFF175" />
                      <path
                        d="M28.1067 24.5623L28.1258 16.8368L24.1937 20.7894L24.6742 20.3063C25.9291 19.0448 28.0829 19.9272 28.0921 21.7066L28.1067 24.5623Z"
                        fill="#0A2238" />
                      <path
                        d="M28.1258 16.8368L22.9755 16.8495L20.4004 16.8559L23.295 16.8592C25.0732 16.8613 25.9647 19.0091 24.7106 20.2697L24.1937 20.7894L28.1258 16.8368Z"
                        fill="#0A2238" />
                      <path
                        d="M16.8563 28.1063L24.1937 20.7894M28.1258 16.8368L22.9755 16.8495L20.4004 16.8559M28.1258 16.8368L28.1067 24.5623M28.1258 16.8368L24.1937 20.7894M17.8252 16.8623L20.4004 16.8559M28.1004 27.1374L28.1067 24.5623M20.4004 16.8559L23.295 16.8592C25.0732 16.8613 25.9647 19.0091 24.7106 20.2697L24.1937 20.7894M28.1067 24.5623L28.0921 21.7066C28.0829 19.9272 25.9291 19.0448 24.6742 20.3063L24.1937 20.7894"
                        stroke="#0A2238"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </a>
                </div>
              </div>
            </article>
          </template>
          <template v-else>
            <div
              v-for="(n, indx) in 8"
              :key="`loading-${indx}`"
              role="status"
              class="loading-skeleton animate-pulse">
              <div
                class="mb-4 h-4 w-full rounded-full bg-gray-200 dark:bg-gray-400"></div>
              <div
                class="mb-10 h-4 w-full rounded-full bg-gray-200 dark:bg-gray-400"></div>
              <div class="mb-6 flex border-b border-black pb-6">
                <div
                  class="mb-4 h-12 w-[40%] rounded-full bg-gray-200 dark:bg-gray-400"></div>
                <div
                  class="ml-2 h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-400"></div>
              </div>
              <span class="sr-only">Loading...</span>
            </div>
          </template>
        </div>
        <div
          class="mt-0 flex w-full items-center justify-start lg:mt-6 lg:justify-center">
          <ArrowCircleButton
            v-if="showMore"
            :disabled="loadingShowMore"
            :link="{
              url: null,
              target: null,
              title: `${loadingShowMore ? 'loading...' : 'Load More'}`,
            }"
            class="back-button cursor-pointer"
            color="blue"
            arrow-direction="down"
            :reverse="false"
            @click="loadMore" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
{
  "NAME": "ArticlesComponent",
  "CLASSNAME": "articles-module-component",
  "URL": "http://vuevitewp.local/"
}
#ENDMETA */

import useGQL from '@/composables/useGQL'
import { ref, watch, onBeforeMount } from 'vue'
import ArrowCircleButton from '@/components/utility/ArrowCircleButton.vue'

const { gqlQuery } = useGQL()
const dataLoading = ref(false)
const lastCursor = ref(null)
const showMore = ref(false)
const loadingShowMore = ref(false)
const allCategories = ref([])
const currentCat = ref(null)
const postData = ref([])
const initialLoading = ref(true)
defineProps(['data'])

const formatDate = (date) => {
  const newDate = new Date(date)
  const options = { year: 'numeric', month: 'long', day: 'numeric' }
  return newDate.toLocaleDateString('en-US', options)
}

const getArticleCategories = async () => {
  const query = `
    query postCategories {
      categories {
        nodes {
          databaseId
          name
          count
        }
      }
    }
  `
  const data = await gqlQuery(query)
  const allCategories = [...data.data.categories.nodes]
  allCategories.push({
    databaseId: 0,
    name: 'All',
    count: data.data.categories.nodes.reduce((acc, cat) => acc + cat.count, 0),
  })
  allCategories.sort((a, b) => a.databaseId - b.databaseId)
  return allCategories
}

const setCurrentCat = (cat) => {
  currentCat.value = cat
}

const getArticles = async (catId, after = null) => {
  dataLoading.value = true
  const postData = await gqlQuery(`
  query getPosts {
    posts(first: 10, ${
      catId.databaseId === 0 ? '' : `where: {categoryId: ${catId.databaseId}}`
    }, after: "${after}") {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        databaseId
        date
        title
        link
        postAcf {
          is_external
          external_link
        }
        categories {
          nodes {
            name
          }
        }
      }
    }
  }
  `)

  lastCursor.value = postData.data.posts.pageInfo.endCursor
  showMore.value = postData.data.posts.pageInfo.hasNextPage
  dataLoading.value = false
  return postData.data.posts.nodes
}

const loadMore = async () => {
  loadingShowMore.value = true
  const news = await getArticles(currentCat.value, lastCursor.value)
  postData.value = [...postData.value, ...news]
  loadingShowMore.value = false
}

watch(currentCat, async () => {
  const news = await getArticles(currentCat.value)
  postData.value = news
})

onBeforeMount(async () => {
  const newsCategories = await getArticleCategories()
  allCategories.value = newsCategories
  currentCat.value = newsCategories[0]
  initialLoading.value = false
})
</script>

<style lang="scss">
.articles-module-component {
  @apply bg-bicara-white;

  .article-inner-container {
    @apply container px-md lg:px-7;
    .articles-container {
      article,
      .loading-skeleton {
        @apply w-full lg:w-[calc(52%-80px)];
        &:nth-child(odd) {
          @apply mr-0 w-full border-r-0 border-black pr-0 lg:mr-10 lg:w-[calc(48%+40px)] lg:border-r lg:pr-10;
        }
      }
      .article-arrow-button {
        &:hover {
          .article-perfect-circle {
            @apply transition-transform duration-500 ease-in-out;
            transform: scale(1.3);
            transform-origin: center;
          }
        }
      }
    }
  }
}
</style>
